import * as React from 'react';
import { WithTooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Single-line truncated text that shows a WithTooltip with the full value
 * only when the element is actually overflowing.
 */
const EllipsisWithTooltip = React.forwardRef(function EllipsisWithTooltip(
  {
    as: Component = 'div',
    text,
    children,
    className,
    side = 'top',
    sideOffset,
    delayDuration,
    contentClassName,
    ...props
  },
  forwardedRef
) {
  const localRef = React.useRef(null);
  const [truncated, setTruncated] = React.useState(false);

  const content =
    text != null
      ? String(text)
      : typeof children === 'string' || typeof children === 'number'
        ? String(children)
        : '';
  const display = children ?? text ?? '';
  const isBlank = !content || content === '—' || !content.trim();

  const setRefs = React.useCallback(
    (node) => {
      localRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  const measure = React.useCallback(() => {
    const el = localRef.current;
    if (!el) {
      setTruncated(false);
      return;
    }
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  React.useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return undefined;

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, content]);

  return (
    <WithTooltip
      content={content}
      disabled={isBlank || !truncated}
      side={side}
      sideOffset={sideOffset}
      delayDuration={delayDuration}
      contentClassName={contentClassName}
    >
      <Component
        ref={setRefs}
        className={cn('min-w-0 overflow-hidden text-ellipsis whitespace-nowrap', className)}
        {...props}
      >
        {display}
      </Component>
    </WithTooltip>
  );
});

EllipsisWithTooltip.displayName = 'EllipsisWithTooltip';

export { EllipsisWithTooltip };
export default EllipsisWithTooltip;
